import { z } from "zod";
import { idSchema, instantSchema, readinessSchema, type contextStatusSchema } from "@amber/contracts";
import { copyRelationshipSchema, type COPY_RELATIONSHIPS } from "./communications.js";

export const COPY_ACTIONS = {
  copies: "evidence.copies",
  carryOver: "evidence.carry_over",
  details: "evidence.correct_details",
  structure: "evidence.correct_structure",
} as const;

export const RELATIONSHIP_LABELS: Record<(typeof COPY_RELATIONSHIPS)[number], string> = {
  full_original: "Full",
  edited_edition: "Longer edit",
  extract: "Clip",
  preview: "Preview",
  syndicated: "Syndicated copy",
  transcript: "Transcript",
  translation: "Translation",
  dub: "Dub",
  synthetic: "Synthetic speech",
};

export const ADDABLE_RELATIONSHIPS = [
  "full_original",
  "edited_edition",
  "extract",
  "preview",
  "dub",
  "translation",
] as const;

const rangeSchema = z
  .tuple([z.number().int().nonnegative(), z.number().int().positive()])
  .refine(([s, e]) => s < e, "The range must start before it ends");

export const addCopyRequestSchema = z.strictObject({
  communication_id: idSchema("com"),
  other_item_id: idSchema("itm"),
  other_communication_id: idSchema("com").optional(),
  range_here_ms: rangeSchema,
  range_there_ms: rangeSchema,
  relationship: z.enum(ADDABLE_RELATIONSHIPS),
  evidence: z.string().trim().min(1, "Name the matching position or add a note"),
  reason: z.string().trim().min(1, "A reason is required"),
});
export type AddCopyRequest = z.input<typeof addCopyRequestSchema>;

export const copyGroupEntrySchema = z.strictObject({
  item_id: idSchema("itm"),
  title: z.string(),
  relationship: z.string(),
  role: z.enum(["primary", "variant"]),
  range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  is_new: z.boolean(),
});

export const addCopyPreviewSchema = z.strictObject({
  communication: z.strictObject({ ordinal: z.number().int().positive(), title: z.string() }),
  grouping: z.array(copyGroupEntrySchema),
  primary_after: z.string(),
  alignment: z.strictObject({ confident: z.number().int().nonnegative(), uncertain: z.number().int().nonnegative() }),
  unaffected: z.array(z.string()),
});
export type AddCopyPreview = z.infer<typeof addCopyPreviewSchema>;

export const makePrimaryRequestSchema = z.strictObject({
  copy_id: idSchema("cpy"),
  reason: z.string().trim().min(1, "A reason is required"),
});
export const makePrimaryPreviewSchema = z.strictObject({
  communication: z.string(),
  from: z.strictObject({
    title: z.string(),
    relationship: z.string(),
    range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  }),
  to: z.strictObject({
    title: z.string(),
    relationship: z.string(),
    range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  }),
  effect: z.string(),
});
export type MakePrimaryPreview = z.infer<typeof makePrimaryPreviewSchema>;

export const separateCopyRequestSchema = z.strictObject({
  copy_id: idSchema("cpy"),
  evidence: z.string().trim().optional(),
  reason: z.string().trim().min(1, "A reason is required"),
});
export const separateCopyPreviewSchema = z.strictObject({
  copy: z.string(),
  range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  readiness: z.array(z.string()),
  exports: z.string(),
  pending_notes: z.number().int().nonnegative(),
});
export type SeparateCopyPreview = z.infer<typeof separateCopyPreviewSchema>;

export const MATCH_DATE_LABELS: Record<string, string> = {
  same_day: "Took place the same day",
  plausible: "Published within days of the event",
  unknown: "The dates don't show whether it is the same occasion",
  differs: "Took place on another day",
  before_event: "Published before the event",
};

export const MATCH_SPEAKER_LABELS: Record<string, string> = {
  consistent: "Same speaker",
  unresolved: "Speakers could not be compared",
  conflicting: "Another speaker",
};

export const decideMatchRequestSchema = z.strictObject({
  copy_id: idSchema("cpy"),
  decision: z.enum(["same", "different"]),
  reason: z.string().trim().min(1, "A reason is required"),
});
export type DecideMatchRequest = z.input<typeof decideMatchRequestSchema>;

export const matchProposalViewSchema = z.strictObject({
  id: idSchema("cpy"),
  other_item_id: idSchema("itm"),
  other_title: z.string(),
  relationship: copyRelationshipSchema,
  relationship_label: z.string(),
  range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  reason: z.string(),
  words: z.number().int().nonnegative(),
  text_score: z.number().min(0).max(1),
  dates: z.string(),
  speakers: z.string(),
  can_decide: z.strictObject({ enabled: z.boolean(), reason: z.string().nullable() }),
});
export type MatchProposalView = z.infer<typeof matchProposalViewSchema>;

export const copyChangeResponseSchema = z.strictObject({
  copy_id: idSchema("cpy"),
  primary_copy_id: idSchema("cpy"),
  message: z.string(),
});
export type CopyChangeResponse = z.infer<typeof copyChangeResponseSchema>;

export const copyCandidateSchema = z.strictObject({
  item_id: idSchema("itm"),
  title: z.string(),
  channel: z.string().nullable(),
  duration_ms: z.number().int().nullable(),
  shared_people: z.array(z.string()),
  communications: z.array(
    z.strictObject({
      id: idSchema("com"),
      ordinal: z.number().int().positive(),
      title: z.string().nullable(),
      range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
    }),
  ),
});
export const copyCandidatesSchema = z.strictObject({ candidates: z.array(copyCandidateSchema) });
export type CopyCandidate = z.infer<typeof copyCandidateSchema>;

export const carryDecisionRequestSchema = z.strictObject({ reason: z.string().trim().min(1, "A reason is required") });
export const carryDecisionResponseSchema = z.strictObject({
  id: idSchema("cov"),
  state: z.enum(["applied", "pending", "deferred", "reverted", "applied_by_editor", "dismissed"]),
  revision_id: idSchema("rev").nullable(),
});

export const carriedSummarySchema = z.strictObject({
  item_id: idSchema("itm"),
  title: z.string(),
  state: z.enum(["applied", "pending", "deferred"]),
});
export type CarriedSummary = z.infer<typeof carriedSummarySchema>;

export const timingTurnSchema = z.strictObject({
  sources: z.array(idSchema("unt")).min(1),
  words: z.tuple([z.number().int().nonnegative(), z.number().int().positive()]).optional(),
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().positive(),
  speaker_id: idSchema("spk").nullable(),
});
export type TimingTurn = z.infer<typeof timingTurnSchema>;

export const MIN_SPLIT_EDGE_MS = 500;

export const structureRequestSchema = z.strictObject({
  lease_id: idSchema("lse"),
  base_revision_id: idSchema("rev"),
  reason: z.string().trim().min(1, "A reason is required"),
  turns: z.array(timingTurnSchema).min(1),
  resolves: z.array(idSchema("rpt")).default([]),
});
export type StructureRequest = z.input<typeof structureRequestSchema>;

export const structureResponseSchema = z.strictObject({
  revision_id: idSchema("rev"),
  version: z.number().int().positive(),
  corrections: z.array(idSchema("cor")),
  kinds: z.array(z.enum(["timing", "turn_split", "turn_merge"])),
  replacements: z.record(idSchema("unt"), z.array(idSchema("unt"))),
  carried: z.array(carriedSummarySchema),
});
export type StructureResponse = z.infer<typeof structureResponseSchema>;

export const splitCommunicationRequestSchema = z.strictObject({
  lease_id: idSchema("lse"),
  base_revision_id: idSchema("rev"),
  at_unit_id: idSchema("unt"),
  reason: z.string().trim().min(1, "A reason is required"),
});
export type SplitCommunicationRequest = z.input<typeof splitCommunicationRequestSchema>;

export const splitPartSchema = z.strictObject({
  range_ms: z.tuple([z.number().int(), z.number().int()]),
  turns: z.number().int().nonnegative(),
  readiness: z.strictObject({ result: readinessSchema, reason: z.string().nullable() }),
  people: z.array(
    z.strictObject({ name: z.string().nullable(), result: readinessSchema, reason: z.string().nullable() }),
  ),
});
export const splitPreviewSchema = z.strictObject({ parts: z.array(splitPartSchema).length(2), copies: z.string() });
export type SplitPreview = z.infer<typeof splitPreviewSchema>;
export const splitResponseSchema = z.strictObject({
  communications: z.array(idSchema("com")).length(2),
  revisions: z.array(idSchema("rev")).length(2),
  correction_id: idSchema("cor"),
});

export const mergeCommunicationRequestSchema = z.strictObject({
  lease_id: idSchema("lse"),
  base_revision_id: idSchema("rev"),
  reason: z.string().trim().min(1, "A reason is required"),
});
export type MergeCommunicationRequest = z.input<typeof mergeCommunicationRequestSchema>;
const mergePartSchema = z.strictObject({
  title: z.string(),
  range_ms: z.tuple([z.number().int(), z.number().int()]),
  turns: z.number().int().nonnegative(),
});
export const mergePreviewSchema = z.strictObject({
  parts: z.array(mergePartSchema).length(2),
  result: mergePartSchema,
  effects: z.array(z.string()),
});
export type MergePreview = z.infer<typeof mergePreviewSchema>;
export const reverseRelationRequestSchema = z.strictObject({
  reason: z.string().trim().min(1, "A reason is required"),
});
export const relationChangeResponseSchema = z.strictObject({
  relation_id: idSchema("spl"),
  communications: z.array(idSchema("com")).min(1),
  message: z.string(),
});
export type RelationChangeResponse = z.infer<typeof relationChangeResponseSchema>;

export const DATE_BASIS_OPTIONS = ["broadcast", "recording", "stated", "sitting", "publication", "other"] as const;
export const DATE_BASIS_LABELS: Record<(typeof DATE_BASIS_OPTIONS)[number], string> = {
  broadcast: "Broadcast time",
  recording: "Recording time",
  stated: "Stated in the content",
  sitting: "Sitting date",
  publication: "Publication date (fallback)",
  other: "Other evidence",
};

export const CONTEXT_OPTIONS = ["complete", "partial", "missing", "unknown"] as const;
export const CONTEXT_OPTION_LABELS: Record<(typeof CONTEXT_OPTIONS)[number], string> = {
  complete: "Complete",
  partial: "Partial",
  missing: "Missing",
  unknown: "Unknown",
};
export const CONTEXT_OPTION_STATUS: Record<(typeof CONTEXT_OPTIONS)[number], z.infer<typeof contextStatusSchema>> = {
  complete: "complete",
  partial: "partial",
  missing: "unavailable_original",
  unknown: "unresolved",
};

export const recordDetailsRequestSchema = z.strictObject({
  lease_id: idSchema("lse"),
  base_revision_id: idSchema("rev"),
  reason: z.string().trim().min(1, "A reason is required"),
  evidence: z.string().trim().min(1, "Supporting evidence is required: a timestamp, paragraph, note or source"),
  profile_links: z
    .array(z.strictObject({ speaker_id: idSchema("spk"), profile_id: idSchema("prf").nullable() }))
    .default([]),
  occurrence: z
    .strictObject({ date: z.iso.date(), basis: z.enum(DATE_BASIS_OPTIONS) })
    .nullable()
    .default(null),
  context: z
    .strictObject({ value: z.enum(CONTEXT_OPTIONS), missing: z.string().trim() })
    .nullable()
    .default(null),
  resolves: z.array(idSchema("rpt")).default([]),
});
export type RecordDetailsRequest = z.input<typeof recordDetailsRequestSchema>;

export const recordDetailsPreviewSchema = z.strictObject({
  changes: z.array(z.strictObject({ field: z.string(), before: z.string(), after: z.string() })),
  affected_turns: z.number().int().nonnegative(),
  checks_to_rerun: z.number().int().nonnegative(),
  consumers: z.string(),
  readiness: z.array(z.strictObject({ name: z.string().nullable(), before: z.string(), after: z.string() })),
});
export type RecordDetailsPreview = z.infer<typeof recordDetailsPreviewSchema>;

export const recordDetailsResponseSchema = z.strictObject({
  revision_id: idSchema("rev"),
  version: z.number().int().positive(),
  corrections: z.array(idSchema("cor")),
});

export const copyViewSchema = z.strictObject({
  id: idSchema("cpy"),
  item_id: idSchema("itm"),
  title: z.string(),
  relationship: copyRelationshipSchema,
  relationship_label: z.string(),
  role: z.enum(["primary", "variant"]),
  range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  current: z.boolean(),
  status: z.string(),
  alignment: z.enum(["aligned", "uncertain", "none", "self"]),
  linked_by: z.string().nullable(),
  can_separate: z.strictObject({ enabled: z.boolean(), reason: z.string().nullable() }),
  can_make_primary: z.strictObject({ enabled: z.boolean(), reason: z.string().nullable() }),
});
export type CopyView = z.infer<typeof copyViewSchema>;

export const carryNoteSchema = z.strictObject({
  id: idSchema("cov"),
  kind: z.enum(["text", "speaker", "timing"]),
  was: z.string(),
  now: z.string(),
  from: z.string(),
  by: z.string(),
  at: instantSchema,
  aligned: z
    .strictObject({
      range_ms: z.tuple([z.number().int(), z.number().int()]),
      confidence: z.enum(["confident", "uncertain"]),
      score: z.number().nullable(),
    })
    .nullable(),
  condition: z.string(),
  task_href: z.string().nullable(),
});
export type CarryNote = z.infer<typeof carryNoteSchema>;
